import {
  Fragment,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Link from "@whoppah/next/link";

import { MegaMenuItemData } from "@/common/contracts/mega-menu.contracts";
import { cn } from "@/common/utils/css.utils";
import { MegaMenuLinkItem } from "@/components/mega-menu/MegaMenuLinkItem";
import { useMegaMenuState } from "@/components/mega-menu/MegaMenuProvider";
import { Heading } from "@/ui/cva/Heading";
import {
  NavigationMenuItem,
  NavigationMenuLink,
} from "@radix-ui/react-navigation-menu";

type MegaMenuLinkItemListProps = {
  depth: number;
  items: MegaMenuItemData[];
  onItemSelect?: (index: number, depth: number) => void;
};

export const MegaMenuLinkItemList = ({
  depth,
  items,
  visible,
  onItemSelect,
}: MegaMenuLinkItemListProps & { visible?: boolean }) => {
  const [subIndex, setSubIndex] = useState(0);

  const { hideMenu } = useMegaMenuState();

  const title = useMemo(() => {
    return items?.find(item => item.type === "links_title");
  }, [items]);

  const links = useMemo(() => {
    return items.filter(item => item.type !== "links_title");
  }, [items]);

  const getSubItems = useCallback(
    (index: number) => {
      const subItemsParent = items[index];

      return {
        id: subItemsParent?.id,
        title: subItemsParent?.label,
        items: subItemsParent?.items ?? [],
      };
    },
    [items]
  );

  const activeSubItems = useMemo(() => {
    return getSubItems(subIndex);
  }, [subIndex, getSubItems]);

  /** Reset selected sub-item to first, each time items has changed. */
  useEffect(() => {
    setSubIndex(0);
  }, [items]);

  /** An item with its own page (`opensOnClick`) navigates even with sub-items. */
  const navigates = (index: number) =>
    !getSubItems(index).items.length || !!items[index]?.opensOnClick;

  const handleSelect = (index: number) => () => {
    const hide = navigates(index);

    setSubIndex(index);
    onItemSelect?.(index, depth);

    if (hide) {
      hideMenu();
    }
  };

  const handleClick =
    (index: number) => (evt: MouseEvent<HTMLAnchorElement>) => {
      if (!navigates(index)) {
        /** If there are sub-items, don't navigate. */
        evt.preventDefault();
      }
    };

  return (
    <>
      <div
        className={cn(
          "flex flex-col items-start space-y-4",
          !visible && "hidden"
        )}
      >
        {title?.href && (
          <ul>
            <NavigationMenuItem>
              <NavigationMenuLink asChild onSelect={hideMenu}>
                <Link href={title.href} prefetch={false}>
                  <Heading
                    as="span"
                    size="h5"
                    className="hover:text-brand-500 hover:underline"
                  >
                    {title.label}
                  </Heading>
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </ul>
        )}
        <ul className="flex flex-col items-start space-y-4">
          {links.map((item, index) => {
            const actualIndex = title?.href ? index + 1 : index;
            const selected = actualIndex === subIndex && !!item.items?.length;

            if (!item.href) {
              return null;
            }

            const href = item.href.startsWith("/http")
              ? item.href.substring(1)
              : item.href;

            return (
              <MegaMenuLinkItem
                key={item.id}
                href={href}
                label={item.label}
                badge={item.badge}
                selected={selected}
                external={href.startsWith("http")}
                onSelect={handleSelect(actualIndex)}
                onClick={handleClick(actualIndex)}
              />
            );
          })}
        </ul>
      </div>
      {links.map(item => {
        if (!item.items?.length) {
          return null;
        }

        /**
         * Every sub-list is rendered on the server (hidden until its parent is
         * selected) so each category URL is a crawlable link on every page,
         * exactly like the force-mounted panels above. Before, sub-lists of a
         * hidden list were skipped, so third-level links only existed in the
         * HTML for the first selected branch.
         */
        const subListVisible = !!visible && item.id === activeSubItems.id;

        return (
          <Fragment key={item.id}>
            <div
              className={cn(
                "border-r border-grey-300",
                !subListVisible && "hidden"
              )}
            />
            <MegaMenuLinkItemList
              key={activeSubItems.id}
              depth={depth + 1}
              items={item.items}
              visible={subListVisible}
              onItemSelect={onItemSelect}
            />
          </Fragment>
        );
      })}
    </>
  );
};
